import { Box, Divider, Typography } from "@mui/material"
import { useNavigate, useSearch } from "@tanstack/react-router"
import type { FC } from "react"
import { type UseFormProps, useForm } from "react-hook-form"

import { StatusFilter } from "@/components/common/Filters"
import { SearchFilterDrawer } from "@/components/common/SearchFilterDrawer/SearchFilterDrawer"
import { PRODUCT_STATUS_LABEL_MAP } from "@/constants/product"
import { ComputedProductStatus, type ProductListFilter } from "@/graphql/codegen/graphql"

const statusList: ComputedProductStatus[] = [
  ComputedProductStatus.Active,
  ComputedProductStatus.Discontinued,
  ComputedProductStatus.PendingApproval,
]

export interface ProductFilterDrawerProps {
  showFilterDrawer: boolean
  toggleDrawer: () => void
}

type FormValues = ProductListFilter & {
  statuses: ComputedProductStatus[] | null
}

export const ProductFilterDrawer: FC<ProductFilterDrawerProps> = ({ showFilterDrawer, toggleDrawer }) => {
  const navigate = useNavigate({ from: "/app/companies/$companyId/edit/products" })
  const search = useSearch({
    from: "/app/companies/$companyId/edit/_layout/products",
    strict: true,
  })

  const { control, reset } = useForm<FormValues>({
    defaultValue: { statuses: [] },
    values: { statuses: search.statuses ?? [] },
  } as UseFormProps<FormValues>)

  const handleChange = (name: keyof FormValues, value: unknown) => {
    navigate({
      search: { ...search, [name]: value },
      replace: true,
    })
  }

  const handleClear = () => {
    reset()
    navigate({
      search: { ...search, statuses: undefined },
      replace: true,
    })
  }

  return (
    <SearchFilterDrawer toggleDrawer={toggleDrawer} open={showFilterDrawer} clear={handleClear}>
      <Box className='flex h-full flex-col gap-y-2'>
        <Box className='flex-none'>
          <Typography variant='subtitle1' fontWeight={700} className='px-4 py-1.5 text-gray-700'>
            Product Status
          </Typography>
          <Divider />
          <StatusFilter
            name='statuses'
            data={statusList}
            control={control}
            onFilterChange={handleChange}
            labelMap={PRODUCT_STATUS_LABEL_MAP}
          />
        </Box>
      </Box>
    </SearchFilterDrawer>
  )
}
